import React, { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, DollarSign, Tag, User, Package, CreditCard, Clock, AlertCircle, CheckCircle, XCircle } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Helmet } from "react-helmet-async";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import { fetchTransactionDetails_Admin } from "@/api/Admin/transaction";
import { TransactionDetailAPI } from "@/api/Admin/type";

const typeStyles: Record<string, { color: string; bg: string }> = {
    investment: {
        color: "text-blue-600 dark:text-blue-400",
        bg: "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800",
    },
    commission: {
        color: "text-purple-600 dark:text-purple-400",
        bg: "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800",
    },
};

const statusStyles: Record<string, { bg: string; text: string; border: string; Icon: React.FC<{ className?: string }> }> = {
    succeed: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", border: "border-green-300 dark:border-green-700", Icon: CheckCircle },
    completed: { bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400", border: "border-green-300 dark:border-green-700", Icon: CheckCircle },
    pending: { bg: "bg-yellow-100 dark:bg-yellow-900/30", text: "text-yellow-700 dark:text-yellow-400", border: "border-yellow-300 dark:border-yellow-700", Icon: Clock },
    failed: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", border: "border-red-300 dark:border-red-700", Icon: XCircle },
    cancelled: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", border: "border-red-300 dark:border-red-700", Icon: XCircle },
    rejected: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", border: "border-red-300 dark:border-red-700", Icon: AlertCircle },
};

const getTransactionTypeLabel = (type: string, t: (key: string, options?: any) => string): JSX.Element => {
    switch (type) {
        case "investment":
            return (
                <span className="text-blue-600 dark:text-blue-400">
                    {t("transactionDetail.types.investment", "Investment")}
                </span>
            );
        case "commission":
            return (
                <span className="text-purple-600 dark:text-purple-400">
                    {t("transactionDetail.types.commission", "Commission")}
                </span>
            );
        case "refund":
            return (
                <span className="text-yellow-600 dark:text-yellow-400">
                    {t("transactionDetail.types.refund", "Refund")}
                </span>
            );
        case "profit":
            return (
                <span className="text-green-600 dark:text-green-400">
                    {t("transactionDetail.types.profit", "Profit")}
                </span>
            );

        default:
            return (
                <span className="text-gray-600 dark:text-gray-400">
                    {t("transactionDetail.types.unknown", "Unknown")}
                </span>
            );
    }
};

const TransactionDetail: React.FC = () => {
    const { txId } = useParams<{ txId: string }>();
    const location = useLocation();
    const { t } = useTranslation();

    const fallbackTx = (location.state as { transaction?: Partial<NonNullable<TransactionDetailAPI["data"]>> } | undefined)?.transaction;
    const [tx, setTx] = useState<NonNullable<TransactionDetailAPI["data"]> | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [fetchError, setFetchError] = useState<boolean>(false);

    useEffect(() => {
        const transactionId = Number(txId);

        if (!transactionId || Number.isNaN(transactionId)) {
            setFetchError(true);
            setLoading(false);
            return;
        }

        setLoading(true);
        setFetchError(false);

        fetchTransactionDetails_Admin(transactionId)
            .then((response) => {
                if (response?.status && response.data) {
                    setTx(response.data);
                    return;
                }

                // Keep graceful fallback for navigation-state data.
                if (fallbackTx) {
                    setTx(fallbackTx as NonNullable<TransactionDetailAPI["data"]>);
                    return;
                }

                setFetchError(true);
            })
            .catch(() => {
                if (fallbackTx) {
                    setTx(fallbackTx as NonNullable<TransactionDetailAPI["data"]>);
                    return;
                }
                setFetchError(true);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [txId, fallbackTx]);

    const displayTx = useMemo(() => tx || (fallbackTx as NonNullable<TransactionDetailAPI["data"]> | undefined) || null, [tx, fallbackTx]);

    const breadcrumbs: BreadcrumbItem[] = [
        { label: "Dashboard", path: "/admin/dashboard" },
        { label: t("transactionDetail.breadcrumbList"), path: "/admin/transactions" },
        { label: t("transactionDetail.title") },
    ];

    if (loading) {
        return (
            <div className="dark:bg-dark-bg min-h-screen p-6 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!displayTx || fetchError) {
        return (
            <div className="dark:bg-dark-bg min-h-screen p-6">
                <div className="text-center text-gray-500 dark:text-gray-400 mt-10">
                    {t("transactionDetail.notFound")}
                </div>
            </div>
        );
    }

    const txType = displayTx.transaction_type as string;
    const txStatus = displayTx.status?.toLowerCase() as string;

    const typeInfo = {
        ...(typeStyles[txType] || { color: "text-gray-600 dark:text-gray-400", bg: "bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700" }),
        label: t(`transactionDetail.types.${txType}`, { defaultValue: txType }),
        description: t(`transactionDetail.types.${txType}Desc`, { defaultValue: "—" }),
    };

    const statusStyleInfo = statusStyles[txStatus] || {
        bg: "bg-gray-100 dark:bg-gray-700/30",
        text: "text-gray-700 dark:text-gray-400",
        border: "border-gray-300 dark:border-gray-600",
        Icon: AlertCircle,
    };
    const statusLabel = t(`transactionDetail.statuses.${txStatus}`, { defaultValue: displayTx.status });

    const isFailed = ["failed", "cancelled", "rejected"].includes(txStatus);

    return (
        <div className="dark:bg-dark-bg min-h-screen">
            <Helmet>
                <title>{t("transactionDetail.title")} - {displayTx.payment_code}</title>
            </Helmet>

            <PageHeader
                title={t("transactionDetail.title")}
                breadcrumbs={breadcrumbs}
            />

            {/* Mã GD + Status + Action — cùng 1 dòng */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t("transactionDetail.codeLabel")} <span className="font-semibold text-blue-600 dark:text-blue-400">{displayTx.payment_code}</span>
                </p>
                <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${statusStyleInfo.bg} ${statusStyleInfo.text} ${statusStyleInfo.border}`}>
                        <statusStyleInfo.Icon className="w-4 h-4" />
                        {statusLabel}
                    </span>
                    <button className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <Download className="w-4 h-4" />
                        {t("transactionDetail.downloadReceipt")}
                    </button>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-6">
                {/* LEFT COLUMN */}
                <div className="flex-1 space-y-5">
                    {/* Transaction type card */}
                    <div className={`rounded-md border p-5 flex items-center justify-between ${typeInfo.bg}`}>
                        <div className="flex items-center gap-4">
                            <div className="p-3 rounded-full bg-white dark:bg-gray-800 shadow-sm">
                                <DollarSign className={`w-5 h-5 ${typeInfo.color}`} />
                            </div>
                            <div>
                                <p className={`text-base font-semibold ${typeInfo.color}`}>{typeInfo.label}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">{typeInfo.description}</p>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t("transactionDetail.amountLabel")}</p>
                            <p className={`text-2xl font-bold ${typeInfo.color}`}>
                                {formatCurrency(displayTx.amount)}
                            </p>
                        </div>
                    </div>

                    {/* Detail info card */}
                    <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-5">
                        <div className="flex items-center gap-2 mb-5">
                            <Tag className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t("transactionDetail.detailInfo")}</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Investor info */}
                            <div>
                                <div className="flex items-center gap-1.5 mb-3">
                                    <User className="w-3.5 h-3.5 text-gray-400" />
                                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t("transactionDetail.investor")}</p>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.investorCode")}</p>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">NDT{displayTx.user_info?.id || "—"}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.fullname")}</p>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{displayTx.user_info?.fullname || "—"}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.email")}</p>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{displayTx.user_info?.email || "—"}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Package info */}
                            <div>
                                <div className="flex items-center gap-1.5 mb-3">
                                    <Package className="w-3.5 h-3.5 text-gray-400" />
                                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t("transactionDetail.investmentPackage")}</p>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.packageName")}</p>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white mt-0.5">{displayTx.source?.user_package?.package_name_snapshot || "—"}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.packageCode")}</p>
                                        <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-0.5">{displayTx.source?.user_package_id ? `PKG-${displayTx.source.user_package_id}` : "—"}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.description")}</p>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{displayTx.description || "—"}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Payment info */}
                            <div>
                                <div className="flex items-center gap-1.5 mb-3">
                                    <CreditCard className="w-3.5 h-3.5 text-gray-400" />
                                    <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">{t("transactionDetail.payment")}</p>
                                </div>
                                <div className="space-y-3">
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.paymentMethod")}</p>
                                        <p className="text-sm text-gray-900 dark:text-white mt-0.5">—</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.bank")}</p>
                                        <p className="text-sm text-gray-900 dark:text-white mt-0.5">—</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("transactionDetail.referenceCode")}</p>
                                        <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mt-0.5">
                                            {displayTx.reference_id ? `REF-${String(displayTx.reference_id).padStart(8, "0")}` : "—"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Note card */}
                    <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-5">
                        <div className="flex items-center gap-2 mb-3">
                            <Tag className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t("transactionDetail.note")}</h2>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                {displayTx.admin_note || (isFailed ? t("transactionDetail.failedNote") : t("transactionDetail.noNote"))}
                            </p>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN */}
                <div className="lg:w-80 space-y-5">
                    {/* Quick info */}
                    <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-5">
                        <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">{t("transactionDetail.quickInfo")}</h2>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500 dark:text-gray-400">{t("transactionDetail.txCode")}</span>
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">{displayTx.payment_code}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500 dark:text-gray-400">{t("transactionDetail.type")}</span>
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">{
                                    getTransactionTypeLabel(txType, t)
                                }</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500 dark:text-gray-400">{t("transactionDetail.amount")}</span>
                                <span className={`text-sm font-bold ${typeInfo.color}`}>{formatCurrency(displayTx.amount)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500 dark:text-gray-400">{t("transactionDetail.txDate")}</span>
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatDate(displayTx.created_at)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 p-5">
                        <div className="flex items-center gap-2 mb-4">
                            <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white">{t("transactionDetail.history")}</h2>
                        </div>
                        <div className="space-y-4">
                            {/* Created event */}
                            <div className="flex gap-3">
                                <div className="flex flex-col items-center">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                                        <div className="w-2.5 h-2.5 rounded-full bg-blue-500"></div>
                                    </div>
                                    {isFailed && <div className="w-0.5 h-full bg-gray-200 dark:bg-gray-700 mt-1"></div>}
                                </div>
                                <div className="pb-4">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t("transactionDetail.created")}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{formatDateTime(displayTx.created_at)}</p>
                                </div>
                            </div>

                            {/* Status event */}
                            {isFailed ? (
                                <div className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                        <XCircle className="w-4 h-4 text-red-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-red-600 dark:text-red-400">{t("transactionDetail.failed")}</p>
                                        <p className="text-xs text-red-500 dark:text-red-400 mt-0.5">
                                            {displayTx.admin_note || t("transactionDetail.checkNote")}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                        <CheckCircle className="w-4 h-4 text-green-500" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-green-600 dark:text-green-400">{t("transactionDetail.succeed")}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{formatDateTime(displayTx.updated_at || displayTx.created_at)}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionDetail;
